import PropTypes from "prop-types"
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { DevTool } from "@hookform/devtools"
import schema from "./Validation"
import { Grid } from "@mui/material"
import { AcademicLevel, Campus, ConsentementMultiple, MentionsLegales, RequiredFields, Submit } from "components"
import { Email, InputText, PhoneNumber, RadioButton } from "components/fields"
import { consent, defaultValues, salutations } from "services"
import { useEffect } from "react"
import RendezVous from "../components/RendezVous"
import Programmes from "../components/Programmes"
import useLandingPage from "../services/useLandingPage"
import { academicLevelsPostBac, campusAvecNice, campusSansNice, LAST_FORMS, PHONE_CALL } from "../services/landingPages"

// Une même LP déclinée sur 3 pages : seuls les campus proposés, le LastForm et l'URL de retour changent
const variants = {
  'programmes-post-bac': {
    lastForm: LAST_FORMS.programmesPostBac,
    campuses: campusAvecNice,
    urlKey: 'ISG-LP-PROGRAMMES-POST-BAC'
  },
  'programmes-post-bac-parcoursup': {
    lastForm: LAST_FORMS.programmesPostBacParcoursup,
    campuses: campusSansNice,
    urlKey: 'ISG-LP-PROGRAMMES-POST-BAC-PSUP'
  },
  'parcoursup-phase-complementaire': {
    lastForm: LAST_FORMS.parcoursupPhaseComplementaire,
    campuses: campusAvecNice,
    urlKey: 'ISG-LP-PSUP-PHASE-COMPLEMENTAIRE'
  }
}

// LP Programmes post-bac : PGE / BMI au choix à Paris, BMI uniquement sur les autres campus
const Form = ({ variant }) => {
  const { campuses, lastForm, urlKey } = variants[variant]
  const methods = useForm({
    defaultValues: {
      ...consent,
      rendezVous: PHONE_CALL,
      lead: {
        ...defaultValues,
        Salutation: '',
        PGE__c: false,
        BMI__c: false
      }
    },
    resolver: yupResolver(schema)
  })
  const { control, setValue, watch } = methods
  const { submit } = useLandingPage({
    campaignStatus: 'Contact Requested',
    formId: 'CONTACT',
    lastForm,
    leadSource: 'LP : Demande de contact',
    urlKey
  })

  const campus = watch('lead.Campus__c')

  useEffect(() => {
    setValue('lead.PGE__c', false)
    setValue('lead.BMI__c', campus !== '' && campus !== 'Paris')
  }, [campus, setValue])

  const onSubmit = data => submit({ ...data, campusLabel: data.lead.Campus__c })

  return (
    <FormProvider {...methods}>
      <DevTool control={control} />
      <Grid container spacing={2}>
        <RendezVous campusLabel="le campus de votre choix" />
        <Grid item xs={12}>
          <RadioButton control={control} label="Civilité" name="lead.Salutation" options={salutations} required />
        </Grid>
        <Grid item sm={6} xs={12}>
          <InputText control={control} label="Prénom" name="lead.FirstName" required />
        </Grid>
        <Grid item sm={6} xs={12}>
          <InputText control={control} label="Nom" name="lead.LastName" required />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Email control={control} label="Adresse e-mail" name="lead.Email" required />
        </Grid>
        <Grid item sm={6} xs={12}>
          <PhoneNumber control={control} name="lead.MobilePhone" label="Téléphone" required />
        </Grid>
        <AcademicLevel options={academicLevelsPostBac} section={false} specialites={false} />
        <Campus label="Campus d'intérêt" options={campuses} />
        {(campus === '' || campus === 'Paris') && (
          <Programmes programmes={[
            { name: 'lead.PGE__c', label: <strong>Programme Grande École en 5 ans.</strong> },
            { name: 'lead.BMI__c', label: <strong>Bachelor en management international.</strong> }
          ]} />
        )}
        <ConsentementMultiple callOptIn />
        <RequiredFields />
        <Submit label="Envoyer" onSubmit={onSubmit} />
        <MentionsLegales form="Contact" school="ISG France" />
      </Grid>
    </FormProvider>
  )
}

Form.propTypes = {
  variant: PropTypes.oneOf(Object.keys(variants)).isRequired
}

export default Form
